import type { Ref } from 'vue'
import { parseCollectionReturnPath } from '~/utils/productListingReturn'

/**
 * Where to send a customer who has nothing left to browse from — the empty
 * states on checkout, in the cart drawer and on the wishlist.
 *
 * Preference order is "the category they were last browsing", then the
 * catalogue-wide listing named in `app.config.ts`. The remembered path is
 * written by `plugins/lastCollection.client.ts` on every navigation.
 */

const STORAGE_KEY = 'last-collection'

/**
 * Shared and reactive rather than a per-caller ref. The cart drawer lives in
 * the default layout and stays mounted for the whole SPA session, so a value
 * read once on mount would pin its link to wherever the visit started and never
 * follow the customer around the catalogue.
 */
export function useLastCollectionPath(): Ref<string | null> {
  return useState<string | null>(STORAGE_KEY, () => null)
}

/**
 * sessionStorage rather than localStorage: "where I was browsing" belongs to
 * this visit. It backs the reactive state above so the path also survives a
 * reload, which drops in-memory state but not the tab. Access is wrapped
 * because hardened browser profiles and Safari's private mode throw on the
 * very first `sessionStorage` touch.
 */
function readStoredPath(): string | null {
  if (!import.meta.client)
    return null

  try {
    return window.sessionStorage.getItem(STORAGE_KEY)
  }
  catch {
    return null
  }
}

/**
 * Remember `fullPath` if it is a category listing; ignore anything else.
 *
 * Takes the state ref rather than resolving it, because the caller is a
 * `router.afterEach` hook where the Nuxt instance is not reliably active.
 */
export function rememberCollectionPath(target: Ref<string | null>, fullPath: string): void {
  if (!import.meta.client)
    return

  const collectionPath = parseCollectionReturnPath(fullPath)
  if (!collectionPath)
    return

  target.value = collectionPath

  try {
    window.sessionStorage.setItem(STORAGE_KEY, collectionPath)
  }
  catch {
    // Storage unavailable — the in-memory value still serves this session, and
    // the fallback listing still gets the customer back after a reload.
  }
}

export function useContinueShopping() {
  const { locale } = useI18n()
  const localePath = useLocalePath()
  const remembered = useLastCollectionPath()

  // The remembered path is deliberately ignored until after mount. The
  // client-only plugin records the entry URL while Nuxt boots — before Vue
  // hydrates — so the shared state can already hold a path that the server
  // never saw. Holding the fallback through hydration keeps both renders
  // identical; the link corrects itself a tick later.
  const hydrated = ref(false)

  onMounted(() => {
    hydrated.value = true
    // Only fills a gap: a navigation this session has already put a fresher
    // value in place, and sessionStorage is what survives a reload.
    remembered.value ??= readStoredPath()
  })

  // The destination when no category has been visited, or when the app config
  // says to always use it. Defaults to `best-sellers`, which is a static route
  // querying `search` with no collectionSlug — it lists the whole catalogue and
  // cannot soft-fail the way an unknown collection slug would.
  const { continueShopping } = useAppConfig()
  const fallbackHref = computed(() => localePath(`/collections/${continueShopping.collectionSlug}`))

  // Re-validated on every read rather than once at write: the stored path is
  // locale-prefixed, and a customer who switches language mid-session must not
  // be sent back into the old one. A rejected path falls through to the
  // fallback, which localePath resolves for the locale now in force.
  const continueShoppingHref = computed(() => {
    if (!hydrated.value || !continueShopping.preferLastVisitedCategory)
      return fallbackHref.value

    return parseCollectionReturnPath(remembered.value, locale.value) ?? fallbackHref.value
  })

  return { continueShoppingHref }
}
